import React from "react"
import { Link } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import Layout from "../Layout"
import {
  HERO,
  WORD_BAND,
  NUMBERS,
  OUR_STORY,
  TIMELINE,
  HOW_WE_WORK,
  LEADERSHIP,
  STORIES_SECTION,
  STORY_TILES,
  PARTNER,
  WHERE_WE_WORK,
  CLOSING,
} from "../../data/about"
import { STORIES } from "../../data/caseStudies"
import "../../styles/aboutPage.scss"

// Mirrors src/styles/_tokens.scss's $ring-size-lg/$ring-size-md (126px /
// 78px) — kept in sync by hand, the same as every other build-time numeric
// value in this codebase that has to match a CSS token (e.g. the photo
// GraphQL queries' own `width:` args against $case-card-photo-h).
const RING_SIZE_LG = 126
const RING_SIZE_MD = 78

// EME/ORR/United Gulf Pipe's tile visuals need fixed (not currentColor)
// fills — kept in sync by hand with _tokens.scss's $navy/$signal/$slate/
// $ink, same reasoning as the ring sizes above.
const COLOR_NAVY = "#1B2D8E"
const COLOR_SIGNAL = "#F0CD17"
const COLOR_SLATE = "#55607A"
const COLOR_INK = "#0F1526"

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 17 17 7M7 7h10v10" />
  </svg>
)

// Sized in em (see &__leader-link-icon), not fixed width/height attributes,
// so it scales with whatever font-size the surrounding link is using.
const LinkedInIcon = () => (
  <svg className="ap__leader-link-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
)

/**
 * A circular progress ring — Numbers section's 95% retention figure, and
 * the same ring reused smaller inside a handful of story tiles. Pure SVG,
 * no image. strokeWidth/valueFontSize default from size (matching the two
 * call sites' own exact spec: 126px/12px-stroke/34px-text,
 * 78px/8px-stroke/19px-text) but can be overridden per spec (e.g. the
 * Muscat Media 100% tile needs 17px instead of 19px).
 */
const ProgressRing = ({ value, size, label, strokeWidth, valueFontSize }) => {
  const stroke = strokeWidth ?? (size >= 110 ? 12 : 8)
  const fontSize = valueFontSize ?? (size >= 110 ? 34 : 19)
  const r = size / 2 - stroke
  const c = 2 * Math.PI * r
  const offset = c - (value / 100) * c

  return (
    <svg className="ap__ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label || `${value}%`}>
      <circle className="ap__ring-track" cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} />
      <circle
        className="ap__ring-fill"
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text className="ap__ring-value" x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fontSize={fontSize}>
        {value}%
      </text>
    </svg>
  )
}

// EME's "three-stage" visual — three numbered circles on a connecting line,
// 190x44, the first two navy and the third yellow (fixed colours, not
// currentColor — the tile is pale-blue, where currentColor is navy and
// wouldn't distinguish the third circle).
const StagesVisual = () => (
  <svg className="ap__tile-stages" width="190" height="44" viewBox="0 0 190 44" fill="none" aria-hidden="true">
    <line x1="16" y1="22" x2="174" y2="22" stroke={COLOR_NAVY} strokeOpacity="0.35" strokeWidth="4" />
    {[
      { cx: 16, color: COLOR_NAVY },
      { cx: 95, color: COLOR_NAVY },
      { cx: 174, color: COLOR_SIGNAL },
    ].map(({ cx, color }, i) => (
      <g key={cx}>
        <circle cx={cx} cy="22" r="16" fill={color} />
        <text x={cx} y="22" textAnchor="middle" dominantBaseline="central" fontSize="16" fontWeight="700" fill={i === 2 ? COLOR_NAVY : "#FFFFFF"}>
          {i + 1}
        </text>
      </g>
    ))}
  </svg>
)

const DecorRing = () => <span className="ap__tile-decor-ring" aria-hidden="true" />

// ORR Protection's visual — three staggered, rounded horizontal bars,
// 190x44.
const BarsVisual = () => (
  <svg className="ap__tile-bars" width="190" height="44" viewBox="0 0 190 44" fill="none" aria-hidden="true">
    <rect x="0" y="2" width="110" height="10" rx="5" fill={COLOR_SIGNAL} />
    <rect x="60" y="17" width="90" height="10" rx="5" fill={COLOR_SLATE} />
    <rect x="20" y="32" width="130" height="10" rx="5" fill={COLOR_NAVY} />
  </svg>
)

// United Gulf Pipe's visual — four ring outlines in a row, 190x44.
const RingsVisual = () => (
  <svg className="ap__tile-rings" width="190" height="44" viewBox="0 0 190 44" fill="none" aria-hidden="true">
    {[
      { cx: 20, color: COLOR_SIGNAL },
      { cx: 70, color: COLOR_NAVY },
      { cx: 120, color: COLOR_SLATE },
      { cx: 170, color: COLOR_INK },
    ].map(({ cx, color }) => (
      <circle key={cx} cx={cx} cy="22" r="16.5" fill="none" stroke={color} strokeWidth="7" />
    ))}
  </svg>
)

/**
 * /about — redesign. Every section's text comes from src/data/about.js;
 * this component only arranges it. Props come from src/pages/about.js's
 * page query:
 *  - heroFloorPhoto/heroDeskPhoto/heroWallPhoto/storyPhoto/odooBadge:
 *    gatsbyImageData, or null if that file wasn't found (see
 *    src/data/about.js for which photos that applies to).
 *  - hasPagePaths: a Set of every real page path on the site (from
 *    allSitePage, trailing slashes stripped) — used both to compute the
 *    Numbers section's case-study-page count and to decide which of
 *    STORY_TILES actually get rendered. Nothing here is hardcoded: add a
 *    case study page later and both update on their own.
 *  - leaders: the existing LEADERS array from src/pages/about.js (name,
 *    title, linkedin, img unchanged) merged with LEADERSHIP.overrides.
 */
const AboutPage = ({ heroFloorPhoto, heroDeskPhoto, heroWallPhoto, storyPhoto, odooBadge, hasPagePaths, leaders }) => {
  const hasPage = path => hasPagePaths.has(path.replace(/\/$/, ""))

  const storyPageCount = [...hasPagePaths].filter(p => p.startsWith("/caseStudies/projects/")).length

  // A tile with `showWithoutPage` still renders once its case study page
  // doesn't exist yet — as a plain non-link block, never a link to a page
  // that isn't there — and becomes a real link automatically the moment
  // `hasPage` starts returning true for it, no code change needed.
  const tiles = STORY_TILES.map(tile => {
    const story = STORIES.find(s => s.name === tile.clientName)
    if (!story) return null
    const pageExists = hasPage(story.path)
    if (!pageExists && !tile.showWithoutPage) return null
    return { ...tile, story, pageExists }
  }).filter(Boolean)

  const featured = tiles[0]
  const rest = tiles.slice(1)

  return (
    <Layout
      pageTitle="Visiomate - About Visiomate"
      descrip="Learn about Visiomate expertise, experience, and commitment to providing innovative solutions."
    >
      <div className="ap">
        {/* 1. HERO */}
        <section className="ap__hero">
          <span className="ap__hero-ring" aria-hidden="true" />
          <div className="ap__hero-inner">
            <div className="ap__hero-text">
              <p className="ap__hero-eyebrow">{HERO.eyebrow}</p>
              <h1 className="ap__hero-heading">{HERO.heading}</h1>
              <p className="ap__hero-body">{HERO.body}</p>
              <div className="ap__hero-ctas">
                <Link className="ap__hero-cta--primary" to={HERO.primaryCta.to}>
                  {HERO.primaryCta.label}
                </Link>
                <Link className="ap__hero-cta--secondary" to={HERO.secondaryCta.to}>
                  {HERO.secondaryCta.label}
                </Link>
              </div>
            </div>
            <div className="ap__hero-photos">
              <div className="ap__hero-photo ap__hero-photo--floor">
                {heroFloorPhoto && (
                  <GatsbyImage
                    image={heroFloorPhoto}
                    alt={HERO.photoFloorAlt}
                    className="ap__hero-photo-img"
                    loading="eager"
                  />
                )}
              </div>
              <div className="ap__hero-photo ap__hero-photo--desk">
                {heroDeskPhoto && (
                  <GatsbyImage
                    image={heroDeskPhoto}
                    alt={HERO.photoDeskAlt}
                    className="ap__hero-photo-img"
                    loading="eager"
                  />
                )}
              </div>
              <div className="ap__hero-photo ap__hero-photo--wall">
                {heroWallPhoto && (
                  <GatsbyImage
                    image={heroWallPhoto}
                    alt={HERO.photoWallAlt}
                    className="ap__hero-photo-img"
                    loading="eager"
                  />
                )}
              </div>
            </div>
          </div>
        </section>

        {/* 2. WORD BAND */}
        <section className="ap__band">
          <div className="ap__band-inner">
            {WORD_BAND.map((word, i) => (
              <React.Fragment key={word}>
                {i > 0 && <span className="ap__band-star" aria-hidden="true">★</span>}
                <span className="ap__band-word">{word}</span>
              </React.Fragment>
            ))}
          </div>
        </section>

        {/* 3. NUMBERS */}
        <section className="ap__numbers">
          <div className="ap__numbers-inner">
            {NUMBERS.map(n => (
              <div className="ap__number" key={n.label}>
                <div className="ap__number-zone">
                  {n.ring ? (
                    <ProgressRing value={n.ring} size={RING_SIZE_LG} label={n.label} />
                  ) : (
                    <b className="ap__number-value">{n.computed ? `${storyPageCount}` : n.value}</b>
                  )}
                </div>
                <span className="ap__number-bar" aria-hidden="true" />
                <span className="ap__number-label">{n.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* 4. OUR STORY */}
        <section className="ap__story">
          <div className="ap__story-inner">
            <div className="ap__story-top">
              <div className="ap__story-photo">
                {storyPhoto && (
                  <GatsbyImage image={storyPhoto} alt={OUR_STORY.photoAlt} className="ap__story-photo-img" loading="lazy" />
                )}
                <span className="ap__story-caption">{OUR_STORY.photoCaption}</span>
              </div>
              <div className="ap__story-copy">
                <p className="ap__story-eyebrow">{OUR_STORY.eyebrow}</p>
                <h2 className="ap__story-heading">{OUR_STORY.heading}</h2>
                <p className="ap__story-body">{OUR_STORY.body}</p>
              </div>
            </div>
            {/* Two independent stacks (not shared rows) — left holds the
                odd years, right the even years, offset 110px lower, which
                is what makes them interleave in date order. */}
            <div className="ap__timeline">
              <div className="ap__timeline-col ap__timeline-col--left">
                {TIMELINE.filter((_, i) => i % 2 === 0).map(entry => (
                  <div
                    key={entry.year}
                    className={`ap__timeline-entry ap__timeline-entry--left${entry.dot === "yellow" ? " ap__timeline-entry--yellow" : ""}`}
                  >
                    <span className="ap__timeline-dot" aria-hidden="true" />
                    <span className="ap__timeline-year">{entry.year}</span>
                    <h3 className="ap__timeline-title">{entry.title}</h3>
                    <p className="ap__timeline-text">{entry.text}</p>
                  </div>
                ))}
              </div>
              <span className="ap__timeline-line" aria-hidden="true" />
              <div className="ap__timeline-col ap__timeline-col--right">
                {TIMELINE.filter((_, i) => i % 2 === 1).map(entry => (
                  <div
                    key={entry.year}
                    className={`ap__timeline-entry ap__timeline-entry--right${entry.dot === "yellow" ? " ap__timeline-entry--yellow" : ""}`}
                  >
                    <span className="ap__timeline-dot" aria-hidden="true" />
                    <span className="ap__timeline-year">{entry.year}</span>
                    <h3 className="ap__timeline-title">{entry.title}</h3>
                    <p className="ap__timeline-text">{entry.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 6. HOW WE WORK */}
        <section className="ap__work">
          <div className="ap__work-inner">
            <p className="ap__work-eyebrow">{HOW_WE_WORK.eyebrow}</p>
            <h2 className="ap__work-heading">{HOW_WE_WORK.heading}</h2>
            <div className="ap__work-grid">
              {HOW_WE_WORK.panels.map(panel => {
                const cornerClass = {
                  "top-left": "ap__work-panel--corner-tl",
                  "top-right": "ap__work-panel--corner-tr",
                  "bottom-left": "ap__work-panel--corner-bl",
                  "bottom-right": "ap__work-panel--corner-br",
                }[panel.corner]
                return (
                  <div
                    key={panel.num}
                    className={`ap__work-panel ap__work-panel--${panel.theme} ${cornerClass}`}
                  >
                    <span className="ap__work-num" aria-hidden="true">{panel.num}</span>
                    <p className="ap__work-label">{panel.label}</p>
                    <p className="ap__work-text">{panel.text}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* 7. LEADERSHIP */}
        <section className="ap__leaders">
          <div className="ap__leaders-inner">
            <p className="ap__leaders-eyebrow">{LEADERSHIP.eyebrow}</p>
            <h2 className="ap__leaders-heading">{LEADERSHIP.heading}</h2>
            <div className="ap__leaders-grid">
              {leaders.map(leader => (
                <div className="ap__leader-card" key={leader.name}>
                  <div className="ap__leader-photo">
                    {leader.img ? (
                      <img src={leader.img} alt={leader.name} className="ap__leader-photo-img" />
                    ) : (
                      <span className="ap__leader-photo-initials" aria-hidden="true">
                        {leader.initials}
                      </span>
                    )}
                  </div>
                  <span className="ap__leader-chip">{leader.title}</span>
                  <h3 className="ap__leader-name">{leader.name}</h3>
                  {/* No credentials -> render nothing, no reserved gap; the
                      next element's own margin-top sets the gap either way. */}
                  {leader.credentials && (
                    <p className="ap__leader-credentials">{leader.credentials}</p>
                  )}
                  <p className="ap__leader-bio">{leader.bio}</p>
                  <a
                    href={leader.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    className="ap__leader-link"
                  >
                    <LinkedInIcon />
                    <span>Connect on LinkedIn</span>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 8. TEAMS THAT RUN ON VISIOMATE */}
        {tiles.length > 0 && (
          <section className="ap__tiles">
            <div className="ap__tiles-inner">
              <div className="ap__tiles-head">
                <div>
                  <p className="ap__tiles-eyebrow">{STORIES_SECTION.eyebrow}</p>
                  <h2 className="ap__tiles-heading">
                    {STORIES_SECTION.heading.split(STORIES_SECTION.highlight)[0]}
                    <span className="ap__tiles-highlight">{STORIES_SECTION.highlight}</span>
                    {STORIES_SECTION.heading.split(STORIES_SECTION.highlight)[1]}
                  </h2>
                </div>
                <div className="ap__tiles-detail">
                  <p className="ap__tiles-detail-text">{STORIES_SECTION.detail}</p>
                  <Link className="ap__tiles-cta" to={STORIES_SECTION.cta.to}>
                    {STORIES_SECTION.cta.label}
                    <ArrowIcon />
                  </Link>
                </div>
              </div>

              <div className="ap__grid">
                {featured && (
                  <Link
                    to={featured.story.path}
                    className={`ap__tile ap__tile--featured ap__tile--${featured.theme}`}
                  >
                    <DecorRing />
                    {/* Above the decorative ring in stacking order — see
                        &__tile-content/&__tile-decor-ring's z-index. */}
                    <div className="ap__tile-content">
                      <span className="ap__tile-arrow"><ArrowIcon /></span>
                      <div className="ap__tile-top">
                        <p className="ap__tile-tag">{featured.tag}</p>
                        <h3 className="ap__tile-name">{featured.story.name}</h3>
                      </div>
                      <div>
                        <div className="ap__tile-figure">
                          <span className="ap__tile-value">{featured.figure.value}</span>
                        </div>
                        <p className="ap__tile-label">{featured.figure.label}</p>
                        {featured.figure2 && (
                          <div className="ap__tile-figure2">
                            <span className="ap__tile-value ap__tile-value--sm">{featured.figure2.value}</span>
                            <p className="ap__tile-label ap__tile-label--sm">{featured.figure2.label}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                )}

                {/* Tile 1 (featured) spans 5 columns/2 rows. Tiles 2-5 sit to
                    its right at 3,4 (row 1) then 4,3 (row 2) — 5+3+4=12 and
                    5+4+3=12. From tile 6 on, 4 columns each (rows of three)
                    — with all 8 tiles present, EME/ORR/United Gulf Pipe form
                    that third row exactly. A short last row stays at this
                    same width and left-aligns via normal grid auto-
                    placement rather than stretching. */}
                {rest.map((tile, i) => {
                  let span
                  if (i === 0) span = 3
                  else if (i === 1) span = 4
                  else if (i === 2) span = 4
                  else if (i === 3) span = 3
                  else span = 4

                  // Still a link once the page exists — never a link to one
                  // that doesn't (showWithoutPage tiles only).
                  const Tag = tile.pageExists ? Link : "div"
                  const linkProps = tile.pageExists ? { to: tile.story.path } : {}

                  return (
                    <Tag
                      key={tile.story.name}
                      className={`ap__tile ap__tile--${tile.theme}`}
                      style={{ gridColumn: `span ${span}` }}
                      {...linkProps}
                    >
                      {tile.pageExists && (
                        <span className="ap__tile-arrow"><ArrowIcon /></span>
                      )}
                      {tile.visual === "ring" ? (
                        <ProgressRing
                          value={tile.figure.value}
                          size={RING_SIZE_MD}
                          label={`${tile.figure.value}%`}
                          valueFontSize={tile.figure.value === 100 ? 17 : 19}
                        />
                      ) : tile.visual === "stages" ? (
                        <StagesVisual />
                      ) : tile.visual === "bars" ? (
                        <BarsVisual />
                      ) : tile.visual === "rings4" ? (
                        <RingsVisual />
                      ) : null}
                      <div>
                        <h3 className="ap__tile-name">{tile.story.name}</h3>
                        <p className="ap__tile-label">{tile.figure.label}</p>
                      </div>
                    </Tag>
                  )
                })}
              </div>
            </div>
          </section>
        )}

        {/* 9. PARTNER + WHERE WE WORK */}
        <section className="ap__partner">
          <div className="ap__partner-inner">
            <div>
              <div className="ap__partner-card">
                {odooBadge ? (
                  <GatsbyImage
                    image={odooBadge}
                    alt="Odoo Ready Partner"
                    className="ap__partner-badge"
                    objectFit="contain"
                  />
                ) : null}
                <div className="ap__partner-copy">
                  <h3 className="ap__partner-heading">{PARTNER.heading}</h3>
                  <p className="ap__partner-text">{PARTNER.body}</p>
                </div>
              </div>
              <p className="ap__where-eyebrow">{WHERE_WE_WORK.eyebrow}</p>
              <h2 className="ap__where-heading">{WHERE_WE_WORK.heading}</h2>
              <p className="ap__where-text">{WHERE_WE_WORK.body}</p>
            </div>
            <div className="ap__regions">
              {WHERE_WE_WORK.regions.map(region => (
                <div className={`ap__region ap__region--${region.theme}`} key={region.name}>
                  <p className="ap__region-name">{region.name}</p>
                  <p className="ap__region-overlap">{region.overlap}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 10. CLOSING BAND */}
        <section className="ap__closing">
          <div className="ap__closing-inner">
            <div>
              <h2 className="ap__closing-heading">{CLOSING.heading}</h2>
              <p className="ap__closing-text">{CLOSING.body}</p>
            </div>
            <Link className="ap__closing-cta" to={CLOSING.cta.to}>
              {CLOSING.cta.label}
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default AboutPage
